import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { parseExcelPastedLines, type ParsedPasteResult } from './salesOrderGridUtils'

export interface PasteExcelModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (lines: ParsedPasteResult['lines']) => void
}

export function PasteExcelModal({
  isOpen,
  onClose,
  onImport,
}: PasteExcelModalProps): React.ReactElement | null {
  const [pasteText, setPasteText] = useState('')
  const [parsed, setParsed] = useState<ParsedPasteResult | null>(null)

  if (!isOpen) return null

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const txt = e.target.value
    setPasteText(txt)
    if (txt.trim()) {
      setParsed(parseExcelPastedLines(txt))
    } else {
      setParsed(null)
    }
  }

  const handleConfirm = () => {
    if (!parsed || parsed.lines.length === 0) return
    onImport(parsed.lines)
    onClose()
  }

  const validCount = parsed ? parsed.lines.filter((l) => l.assembly_type && l.ista_level && l.quantity > 0).length : 0

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-slate-900/60 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-4xl rounded-lg bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">Dán từ Excel (Bảng dòng đơn hàng)</h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="p-5 text-sm space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <p className="text-xs text-slate-600 mb-2 font-medium">
              Sao chép các dòng trong file Excel rồi dán (Ctrl+V) vào khung dưới đây. Thứ tự các cột:
              <br />
              <strong className="text-emerald-800 font-mono">
                1. Khách hàng | 2. Mã hàng | 3. Ráp/Tháo | 4. ISTA | 5. Số lượng | 6. PO khách
              </strong>
            </p>
            <textarea
              className="w-full rounded-md border border-slate-300 p-2.5 font-mono text-xs outline-none focus:border-emerald-600"
              rows={5}
              placeholder="Dán dữ liệu từ bảng tính vào đây..."
              value={pasteText}
              onChange={handleTextChange}
              autoFocus
            />
          </div>

          {parsed && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-700 text-xs">
                  Xem trước: {parsed.lines.length} dòng ({validCount} hợp lệ, {parsed.errors.length} cảnh báo)
                </span>
              </div>

              {parsed.errors.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 max-h-24 overflow-y-auto">
                  {parsed.errors.map((err, i) => (
                    <div key={i}>
                      • Dòng {err.row}: {err.message}
                    </div>
                  ))}
                </div>
              )}

              <div className="max-h-60 overflow-x-auto overflow-y-auto border border-slate-200 rounded-md">
                <table className="w-full text-xs text-left border-collapse">
                  <thead className="bg-slate-100 text-slate-700 font-bold sticky top-0">
                    <tr>
                      <th className="p-2 border-b border-r border-slate-200">#</th>
                      <th className="p-2 border-b border-r border-slate-200">Khách hàng</th>
                      <th className="p-2 border-b border-r border-slate-200">Mã hàng</th>
                      <th className="p-2 border-b border-r border-slate-200">Ráp/Tháo</th>
                      <th className="p-2 border-b border-r border-slate-200">ISTA</th>
                      <th className="p-2 border-b border-r border-slate-200 text-right">Số lượng</th>
                      <th className="p-2 border-b border-slate-200">PO khách</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.lines.map((row, idx) => {
                      const isValid = row.customer_code && row.variant_code && row.assembly_type && row.ista_level && row.quantity > 0
                      return (
                        <tr key={idx} className={isValid ? 'hover:bg-slate-50' : 'bg-rose-50 text-rose-800'}>
                          <td className="p-2 border-b border-r border-slate-200 text-center font-mono">{idx + 1}</td>
                          <td className="p-2 border-b border-r border-slate-200 font-mono font-bold">{row.customer_code || '—'}</td>
                          <td className="p-2 border-b border-r border-slate-200 font-mono font-bold text-emerald-700">{row.variant_code || '—'}</td>
                          <td className="p-2 border-b border-r border-slate-200">{row.assembly_type === 'assembled' ? 'Ráp chết' : row.assembly_type === 'kd' ? 'Tháo rời' : '—'}</td>
                          <td className="p-2 border-b border-r border-slate-200">{row.ista_level ? `ISTA ${row.ista_level}` : '—'}</td>
                          <td className="p-2 border-b border-r border-slate-200 text-right font-mono font-bold">{row.quantity || '—'}</td>
                          <td className="p-2 border-b border-slate-200 font-mono">{row.customer_po || '—'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button variant="secondary" size="sm" type="button" onClick={onClose}>
            Đóng
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="button"
            disabled={!parsed || parsed.lines.length === 0}
            onClick={handleConfirm}
          >
            Nhập vào bảng ({parsed?.lines.length || 0} dòng)
          </Button>
        </div>
      </div>
    </div>
  )
}
